import { getRole, type Role } from './role'
import * as api from '@owl-nest/api-client/next'

type RuleContext = { project: api.Project; role: Role }

type Rule = (context: RuleContext) => boolean

export type BaseRules = Record<string, { _access: Rule } & Record<string, Rule | Record<string, Rule>>>

export type ScreenOf<RULES extends BaseRules> = keyof RULES

export type Query<
  RULES extends BaseRules,
  SCREEN extends keyof RULES,
  FEATURE extends keyof RULES[SCREEN],
  ACTION extends keyof RULES[SCREEN][FEATURE],
> = {
  screen: SCREEN
} & (
  | {
      feature?: undefined
      action?: undefined
    }
  | ({
      feature: FEATURE
    } & (RULES[SCREEN][FEATURE] extends Rule ? { action?: undefined } : { action: ACTION }))
)

export interface AccessControlHandler<RULES extends BaseRules> {
  hasAccessTo<
    SCREEN extends keyof RULES,
    FEATURE extends keyof RULES[SCREEN],
    ACTION extends keyof RULES[SCREEN][FEATURE],
  >(
    query: Query<RULES, SCREEN, FEATURE, ACTION>,
  ): boolean
}

export type AccessControlConfig<RULES extends BaseRules> = {
  project: api.Project
  role: api.UserRole
  isStaff: boolean
  rules: RULES
}

export function createAccessControlHandler<RULES extends BaseRules>(
  config: AccessControlConfig<RULES>,
): AccessControlHandler<RULES> {
  const role = getRole(config.role, config.isStaff)
  const project = config.project

  return {
    hasAccessTo(query) {
      const screenRules = config.rules[query.screen]

      if (screenRules === undefined) {
        throw new Error(`Config error, no config found for screen "${String(query.screen)}"`)
      }

      if (query.feature === undefined) {
        if (query.action !== undefined) {
          throw new Error("Query error, can't query for action without feature")
        }
        if (!('_access' in screenRules)) {
          throw new Error(`Config error, no "_access" rule found in screen "${String(query.screen)}"`)
        }
        return screenRules['_access']({ project, role })
      }

      const featureRules = screenRules[query.feature]

      if (screenRules === undefined) {
        throw new Error(
          `Config error, no config found for feature "${String(query.feature)}" of screen "${String(query.screen)}"`,
        )
      }

      if (typeof featureRules === 'function') {
        if (query.action !== undefined) {
          throw new Error(
            `Query error, can't query for action for action-less feature "${String(query.feature)}" of screen "${String(query.screen)}"`,
          )
        }
        return featureRules({ project, role })
      } else {
        if (query.action === undefined) {
          throw new Error(
            `Query error, missing action for feature "${String(query.feature)}" of screen "${String(query.screen)}"`,
          )
        }

        const actionRule = (featureRules as any)[query.action]

        if (actionRule === undefined) {
          throw new Error(
            `Config error, no config found for action "${String(query.action)}" of feature "${String(query.feature)}" of screen "${String(query.screen)}"`,
          )
        }

        return actionRule({ project, role })
      }
    },
  }
}
